import { useState } from 'react';
import { logWeight } from '../services/weightApi';

interface WeightFormProps {
  onLogged: () => void;
}

function toLocalDatetimeValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

export function WeightForm({ onLogged }: WeightFormProps) {
  const [weight, setWeight] = useState('');
  const [recordedAt, setRecordedAt] = useState(() => toLocalDatetimeValue(new Date()));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const lbs = parseFloat(weight);
    if (!weight || isNaN(lbs) || lbs <= 0) {
      setError('Please enter a valid weight.');
      return;
    }

    setSubmitting(true);
    try {
      await logWeight(lbs, new Date(recordedAt).toISOString());
      setWeight('');
      setRecordedAt(toLocalDatetimeValue(new Date()));
      onLogged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save weight.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6"
    >
      <h2 className="text-base font-semibold text-gray-900 mb-4">Log Weight</h2>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-500 mb-1" htmlFor="weight-lbs">
            Weight (lbs)
          </label>
          <input
            id="weight-lbs"
            type="number"
            step="0.1"
            min="1"
            max="1500"
            placeholder="e.g. 175.5"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
          />
        </div>

        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-500 mb-1" htmlFor="recorded-at">
            Date &amp; Time
          </label>
          <input
            id="recorded-at"
            type="datetime-local"
            value={recordedAt}
            onChange={(e) => setRecordedAt(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
          />
        </div>

        <div className="flex items-end">
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-600">{error}</p>
      )}
    </form>
  );
}
